import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  CircularProgress,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { CustomerService } from '../../../services/customer.service';
import { CustomerTypes } from '../../../types/customer.types';
import {
  v2SettingsApi,
  type CustomerOrgTreeResponse,
  type OrgTreeTeam,
} from './v2-settings-api';
import { EntityOverrideEditor, type EditableLevel } from './entity-override-editor';

// Sentinel for "teams attached directly to the customer with no parent group".
const NO_GROUP = '__none__';

// Filter styling matched to the shared UnifiedFilterBar used on the POM Users
// and Alerts pages: a tiny uppercase label above each control, small Poppins
// inputs, 6px radius, subtle border.
const FILTER_LABEL_SX = {
  fontSize: '10px',
  color: '#8A94A6',
  fontWeight: 500,
  textTransform: 'uppercase' as const,
  letterSpacing: '.5px',
};
const FILTER_INPUT_SX = {
  '& .MuiInputBase-root': { fontSize: '13px', fontFamily: 'Poppins, sans-serif', borderRadius: '6px' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E8ECF0' },
};

/**
 * Single unified overrides editor for the POM Master settings UI.
 * Pick a customer once, then switch freely between editing at the customer,
 * group, or team level without losing context. Cascading Selects keep the
 * picker UI scoped to what's relevant for the current level.
 */
export const OverridesEditor: React.FC = () => {
  const customers = useSelector(
    (s: RootState) => (s.customersAdmin?.pagination?.items ?? []) as CustomerTypes.Model[],
  );

  const [customerId, setCustomerId] = useState<string>('');
  const [groupId, setGroupId] = useState<string>('');
  const [teamId, setTeamId] = useState<string>('');
  const [level, setLevel] = useState<EditableLevel>('customer');
  const [orgTree, setOrgTree] = useState<CustomerOrgTreeResponse | null>(null);
  const [treeLoading, setTreeLoading] = useState(false);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Customers list — load once.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setCustomersLoading(true);
        await CustomerService.fetchMany({ page: 1, perPage: 500, filter: { name: '', city: '', state: '', zip: '' } });
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.message || err.message);
      } finally {
        if (!cancelled) setCustomersLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Reload org tree when customer changes, and reset downstream picks.
  useEffect(() => {
    let cancelled = false;
    setGroupId('');
    setTeamId('');
    setOrgTree(null);
    if (!customerId) return;
    (async () => {
      setTreeLoading(true);
      setError(null);
      try {
        const tree = await v2SettingsApi.getCustomerOrgTree(customerId);
        if (!cancelled) setOrgTree(tree);
      } catch (err: any) {
        if (!cancelled) setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
      } finally {
        if (!cancelled) setTreeLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [customerId]);

  // Reset team when group picker changes.
  useEffect(() => { setTeamId(''); }, [groupId]);

  const sortedCustomers = useMemo(
    () => [...customers].sort((a, b) => (a.name || '').localeCompare(b.name || '')),
    [customers],
  );

  const availableTeams: OrgTreeTeam[] = useMemo(() => {
    if (!orgTree) return [];
    if (groupId === NO_GROUP) return orgTree.teamsWithoutGroup;
    if (groupId) return orgTree.groups.find((g) => g.id === groupId)?.teams ?? [];
    return [];
  }, [orgTree, groupId]);

  // Which entity is being edited depends on the chosen level.
  const entityId =
    level === 'team' ? (teamId || null)
    : level === 'group' ? (groupId && groupId !== NO_GROUP ? groupId : null)
    : (customerId || null);

  // Resolve the cascade only as deep as the level being edited. Sending the
  // full picked chain regardless of level would let a deeper override "win"
  // over the level you're editing — e.g. editing Customer but seeing
  // Source: group (and the group's value) because a group was still selected.
  // Truncating keeps Source/Value honest to the level you can actually write.
  const effectiveParams =
    level === 'team'
      ? {
          customerId: customerId || undefined,
          ...(groupId && groupId !== NO_GROUP ? { groupId } : {}),
          ...(teamId ? { teamId } : {}),
        }
      : level === 'group'
        ? {
            customerId: customerId || undefined,
            ...(groupId && groupId !== NO_GROUP ? { groupId } : {}),
          }
        : { customerId: customerId || undefined };

  // Each level has its own empty-state copy so the user knows what to pick.
  const emptyStateMessage =
    !customerId ? 'Pick a customer to begin.'
    : level === 'group' && !groupId ? 'Pick a group to view and edit its overrides.'
    : level === 'team' && !groupId ? 'Pick a group (or "none" for direct-on-customer teams) and then a team.'
    : level === 'team' && !teamId ? 'Pick a team to view and edit its overrides.'
    : undefined;

  const header = (
    <Stack spacing={2} sx={{ mb: 2 }}>
      <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap">
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Typography sx={FILTER_LABEL_SX}>Customer</Typography>
          <Autocomplete
            size="small"
            sx={{ minWidth: 260, ...FILTER_INPUT_SX }}
            options={sortedCustomers}
            loading={customersLoading}
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={sortedCustomers.find((c) => c.id === customerId) ?? null}
            onChange={(_, v) => setCustomerId(v?.id ?? '')}
            renderInput={(params) => <TextField {...params} placeholder="Search customers…" />}
          />
        </Box>
        {treeLoading && <CircularProgress size={18} sx={{ mb: 0.5 }} />}
      </Stack>

      {customerId && (
        <Stack direction="row" spacing={2} alignItems="flex-end" flexWrap="wrap">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            <Typography sx={FILTER_LABEL_SX}>Editing at level</Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={level}
              onChange={(_, v) => v && setLevel(v as EditableLevel)}
            >
              <ToggleButton value="customer" sx={{ px: 2, fontWeight: 600 }}>Customer</ToggleButton>
              <ToggleButton value="group" sx={{ px: 2, fontWeight: 600 }}>Group</ToggleButton>
              <ToggleButton value="team" sx={{ px: 2, fontWeight: 600 }}>Team</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {(level === 'group' || level === 'team') && (() => {
            const groupOptions: Array<{ id: string; name: string }> = [
              ...(level === 'team' && orgTree?.teamsWithoutGroup?.length
                ? [{ id: NO_GROUP, name: '(none — teams direct on customer)' }]
                : []),
              ...(orgTree?.groups ?? []).map((g) => ({ id: g.id, name: g.name })),
            ];
            return (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                <Typography sx={FILTER_LABEL_SX}>Group</Typography>
                <Autocomplete
                  size="small"
                  sx={{ minWidth: 220, ...FILTER_INPUT_SX }}
                  options={groupOptions}
                  disabled={!orgTree || treeLoading}
                  getOptionLabel={(o) => o.name || ''}
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={groupOptions.find((g) => g.id === groupId) ?? null}
                  onChange={(_, v) => setGroupId(v?.id ?? '')}
                  renderInput={(params) => <TextField {...params} placeholder="Search groups…" />}
                />
              </Box>
            );
          })()}

          {level === 'team' && groupId && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Typography sx={FILTER_LABEL_SX}>Team</Typography>
              <Autocomplete
                size="small"
                sx={{ minWidth: 220, ...FILTER_INPUT_SX }}
                options={availableTeams}
                disabled={availableTeams.length === 0}
                getOptionLabel={(o) => o.name || ''}
                isOptionEqualToValue={(o, v) => o.id === v.id}
                value={availableTeams.find((t) => t.id === teamId) ?? null}
                onChange={(_, v) => setTeamId(v?.id ?? '')}
                renderInput={(params) => <TextField {...params} placeholder="Search teams…" />}
              />
            </Box>
          )}

          {customerId && orgTree && level === 'group' && orgTree.groups.length === 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>No groups in this customer</Typography>
          )}
        </Stack>
      )}

      {error && <Typography variant="caption" color="error">{error}</Typography>}
    </Stack>
  );

  return (
    <Box>
      <EntityOverrideEditor
        level={level}
        entityId={entityId}
        effectiveParams={effectiveParams}
        header={header}
        emptyStateMessage={emptyStateMessage}
      />
    </Box>
  );
};
